import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

/*
  Zadanie typu:
    B^{log_a C}

  gdzie:
    B = a^p  (lub 1 / a^p, np. (1/2)^{log_2 9})
  i korzystamy ze schematu:
    (a^p)^{log_a C} = a^{p log_a C} = a^{log_a C^p} = C^p.

  Chcemy, żeby:
    - podstawa potęgi NIE była równa bezpośrednio podstawie logarytmu,
    - liczby były „ładne” (wyniki typu 4, 9, 25, 1/4, 1/9 itd.),
    - logarytm w wykładniku NIE był „prosty” (C nie jest potęgą a),
      żeby uczeń MUSIAŁ użyć własności a^{log_a b} = b.
*/

// ───────── HELPERY ─────────

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function toFraction(x, maxDen = 1000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };

  let h0 = 0, k0 = 1;
  let h1 = 1, k1 = 0;
  let y = x;

  for (let i = 0; i < 16; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1;
    k0 = k1;
    h1 = h2;
    k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }

  let num = h1;
  let den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0;
    den = k0;
  }

  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);

  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);

  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));

  const sign = num < 0 ? '-' : '';
  num = Math.abs(num);

  return `${sign}\\tfrac{${num}}{${den}}`;
}

// jeśli baza jest ułamkiem, dokładamy nawiasy ( ... )
function withParensIfFraction(baseLatex) {
  return /\\dfrac|\\frac|\\tfrac/.test(baseLatex)
    ? `\\left(${baseLatex}\\right)`
    : baseLatex;
}

const logb = (a) => `\\log_{${a}}`;

// CZY C JEST POTĘGĄ a (całkowitą, dodatnią)?
function isIntegerPowerOfBase(C, a) {
  if (C <= 0) return false;
  let v = 1;
  for (let k = 0; k <= 10; k++) { //  a^10 i tak > 9 dla naszych a
    if (v === C) return true;
    v *= a;
  }
  return false;
}

/*
  Scenariusze:
   - baza logarytmu a ∈ {2,3,5},
   - wykładnik p ∈ {2, -1} (żeby wyniki były sensowne),
   - C z listy ładnych liczb: 2–9, ALE NIE potęgi a.
*/
const COMMON_C = [2, 3, 4, 5, 6, 7, 8, 9];

const SCENARIOS = [
  { a: 2, p: 2, Cs: COMMON_C },
  { a: 2, p: -1, Cs: COMMON_C },
  { a: 3, p: 2, Cs: COMMON_C },
  { a: 3, p: -1, Cs: COMMON_C },
  { a: 5, p: 2, Cs: COMMON_C },
  { a: 5, p: -1, Cs: COMMON_C },
];

export function generateTask() {
  const scenario = pick(SCENARIOS);
  const { a, p, Cs } = scenario;

  // odrzucamy C będące potęgami a (żeby log_a C nie był „oczywisty”)
  const filteredCs = Cs.filter((c) => !isIntegerPowerOfBase(c, a));
  const pool = filteredCs.length > 0 ? filteredCs : Cs;

  const C = pick(pool);

  // baza potęgi do wypisania:
  let baseLatex;
  if (p > 0) {
    const baseVal = Math.pow(a, p);
    baseLatex = niceLatexFromNumber(baseVal); // np. 4, 9, 25
  } else {
    const k = -p;
    const baseVal = 1 / Math.pow(a, k);
    baseLatex = niceLatexFromNumber(baseVal); // np. 1/2, 1/9
  }

  const C_latex = niceLatexFromNumber(C);
  const resultVal = Math.pow(C, p);          // C^p – końcowy wynik

  return {
    type: 'LogPropPowerLogTask',
    data: {
      a,
      p,
      baseLatex,
      C,
      C_latex,
      resultVal,
    },
  };
}

/* ───────── renderTask ───────── */

export function renderTask({ data }) {
  const { a, baseLatex, C_latex } = data;

  const baseDisplay = withParensIfFraction(baseLatex);
  const expr = `${baseDisplay}^{${logb(a)} ${C_latex}}`;

  return (
    <div>
      Oblicz wartość wyrażenia:
      <div style={{ marginTop: 14, marginBottom: 14 }}>
        <BlockMath math={expr} />
      </div>
    </div>
  );
}

/* ───────── input / check / answers ───────── */

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const v = userAnswer.parsed;
  if (v == null) return false;
  return Math.abs(v - data.resultVal) < 1e-4;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={niceLatexFromNumber(data.resultVal)} />;
}

/* ───────── explanation ───────── */

export function renderExplanation({ data }) {
  const { a, p, baseLatex, C_latex, resultVal } = data;

  const pLatex = niceLatexFromNumber(p);
  const resultLatex = niceLatexFromNumber(resultVal);

  const logPart = `${logb(a)} ${C_latex}`;
  const baseAsPower = `${a}^{${pLatex}}`;
  const baseDisplay = withParensIfFraction(baseLatex);

  // (a^p)^(log_a C)
  const step2Right = `\\left(${baseAsPower}\\right)^{${logPart}}`;

  // a^{p log_a C} = a^{log_a C^{p}}
  const step3Right = `${a}^{${pLatex}\\,${logPart}} = ${a}^{${logb(a)} ${C_latex}^{${pLatex}}}`;

  return (
    <div className="space-y-2">

      <p><strong>Krok 1:</strong> Skorzystamy ze wzoru na potęgę z logarytmem:</p>
      <BlockMath math={'a^{\\log_{a} b} = b'} />

      <p><strong>Krok 2:</strong> Zapisz podstawę potęgi jako potęgę podstawy logarytmu:</p>
      <BlockMath
        math={
          `${baseDisplay}^{${logPart}} = ${step2Right}`
        }
      />

      <p><strong>Krok 3:</strong> Przenieś wykładnik przed logarytm i połącz w jeden logarytm:</p>
      <BlockMath math={`${step2Right} = ${step3Right}`} />

      <p>
        <strong>Krok 4:</strong> Zastosuj wzór{' '}
        <InlineMath math={'a^{\\log_{a} b} = b'} />:
      </p>

      <BlockMath
        math={`${a}^{${logb(a)} ${C_latex}^{${pLatex}}} = ${C_latex}^{${pLatex}}`}
      />

      <p><strong>Krok 5:</strong> Oblicz otrzymaną potęgę:</p>
      <BlockMath math={`${C_latex}^{${pLatex}} = ${resultLatex}`} />

      <p><strong>Wniosek:</strong></p>
      <BlockMath
        math={`${baseDisplay}^{${logPart}} = ${resultLatex}`}
      />
    </div>
  );
}
